// Central content + asset registry for the Hacktonomy landing page.
import rhino from "./rhino.webp";
import cyberfrat from "./cyberfrat.webp";
import hcaspHero from "./hcaspHero.png";
import abstractAI from "./abstractAI.jpg";
import cyberPro from "./cyberPro.jpg";


export const ASSETS = {
  rhino,
  cyberfrat,
  hcaspHero,
  abstractAI,
  cyberPro,

};

export const NAV_LINKS = [
  { id: "about-hcasp", label: "About" },
  { id: "highlights", label: "Highlights" },
  { id: "exam-info", label: "Exam" },
  { id: "curriculum", label: "Curriculum" },
  { id: "pricing", label: "Pricing" },
];

export const HIGHLIGHTS = [
  {
    n: "01",
    title: "Vendor Neutral & Foundational",
    body: "Learn essential principles that apply across all major LLMs, RAG pipelines, and Agentic frameworks. ",
  },
  {
    n: "02",
    title: "Practical & Scenario Based",
    body: "Designed around real world application scenarios to test practical security awareness rather than abstract theory.",
  },
  {
    n: "03",
    title: "Comprehensive 5 Domain Scope",
    body: " Covers foundational AI architecture, prompt attacks, autonomous agent security, Model Context Protocol (MCP), NIST AI RMF basics, and operational guardrails.",
  },
  {
    n: "04",
    title: "Accessible Entry Point",
    body: "Perfect for IT and cybersecurity professionals transitioning into the AI security domain without requiring an advanced ML or mathematics background. ",
  },
  {
    n: "05",
    title: "Career Alignment",
    body: "Prepares candidates for entry to mid level roles in AI security, DevSecOps, SOC analysis, and AI application auditing. ",
  },
  {
    n: "06",
    title: "CPE Backed Professional Growth",
    body: "18 month certification lifecycle paired with a clear 30 CPE renewal pathway for ongoing professional development.",
  },
];

export const WHO_SHOULD_ATTEND = [
  {
    role: "Cybersecurity Beginners & IT Professionals ",
    desc: "entering the AI security domain.",
  },
  {
    role: "Junior to Mid Penetration Testers & Security Analysts ",
    desc: "learning GenAI vulnerability testing.",
  },
  {
    role: "Software Developers & QA Engineers",
    desc: "utilizing AI code assistants Vibe Coding and building LLM integrated apps. ",
  },
  {
    role: "SOC Analysts & Incident Responders ",
    desc: "monitoring LLM traffic and handling basic prompt injection alerts. ",
  },
  {
    role: "IT Managers, Risk Officers & Compliance Analysts",
    desc: "seeking practical awareness of the NIST AI RMF and EU AI Act. ",
  },
];

export const EXPERIENCE_REQS = [
  {
    title: "Prerequisites",
    body: "Basic understanding of IT security concepts, fundamental Python awareness, and basic familiarity with REST APIs. ",
  },
  {
    title: "No ML Math Required",
    body: "No prior expertise in advanced linear algebra, deep learning math, or model training algorithms is required. ",
  },
  {
    title: "Python & API Awareness",
    body: "Fundamental Python awareness and basic familiarity with REST APIs, JSON payloads, and HTTP request structures. ",
  },
  {
    title: "Analytical Mindset",
    body: "A practical security mindset focused on analyzing application logic, evaluating prompt guardrails, and auditing AI workflows. ",
  },

];

export const EXAM_INFO = [
  { label: "Format", value: "100 Multiple Choice Questions (MCQs)" },
  { label: "Level", value: "Beginner to Intermediate" },
  { label: "Question type", value: "Scenario based practical questions (No simple rote memorization)" },
  { label: "Duration", value: "120 Minutes (2 Hours)" },
  { label: "Passing score", value: "75% or higher" },
  { label: "Delivery method", value: "Online, remotely proctored environment" },
  { label: "Certification validity", value: "18 Months (Requires 30 CPE credits for renewal)" },
];

export const DOMAINS = [
  {
    n: "01",
    weight: "15%",
    title: "AI Fundamentals & Architecture",
    short: "Foundations",
    groups: [
      {
        heading: "1.1 AI & GenAI Fundamentals",
        items: [
          "Transitioning from hype to hands on mastery: real world applications of AI.",
          "Essential concepts in Machine Learning (ML) and Deep Learning (DL).",
          "Transformer architectures, RAG (Retrieval Augmented Generation) and Agentic frameworks.",
        ],
      },
      {
        heading: "1.2 Interaction and Automation",
        items: [
          "Prompt engineering techniques: from beginner concepts to advanced structural prompt design.",
          "Automation using AI workflows for business and operational tasks.",
          "Building structured prompts for content creation, summarization and automation.",
        ],
      },
      {
        heading: "1.3 Understanding LLM Behavior",
        items: [
          "Mechanics of Large Language Models (LLMs).",
          "Tokenization, Context Windows and Vector Space.",
          "Understanding and identifying AI Hallucinations.",
        ],
      },
    ],
  },
  {
    n: "02",
    weight: "30%",
    title: "AI Security & Threat Landscape",
    short: "Offense",
    groups: [
      {
        heading: "2.1 The AI Threat Landscape",
        items: [
          "Comprehensive review of the OWASP Top 10 for LLMs.",
          "Vulnerability mapping and attack surface analysis for AI systems.",
        ],
      },
      {
        heading: "2.2 Advanced Generative AI Red Teaming",
        items: [
          "Direct vs. Indirect Prompt Injection.",
          "Exploiting RAG pipelines via poisoned documents.",
          "Data extraction techniques: system prompt extraction and PII leakage.",
          "Agentic exploitation, including LLM driven Server Side Request Forgery (SSRF).",
        ],
      },
      {
        heading: "2.3 Deep Learning Vulnerabilities",
        items: [
          "Adversarial Evasion Attacks (gradient based attacks, black box attacks).",
          "Model Inversion and Membership Inference.",
          "Model Extraction, Oracle attacks and Data Poisoning at scale.",
        ],
      },
    ],
  },
  {
    n: "03",
    weight: "20%",
    title: "Secure AI Engineering & Agentic Orchestration",
    short: "Engineering",
    groups: [
      {
        heading: "3.1 Defensive AI Engineering & Guardrails",
        items: [
          "Implementing robust guardrails: input sanitization, output filtering and semantic firewalls.",
          "Secure adoption of AI in enterprise environments and measuring the ROI of AI security.",
        ],
      },
      {
        heading: "3.2 Securing the AI Supply Chain",
        items: [
          "Securing the ML Supply Chain and MLOps pipelines (AI SBOMs).",
        ],
      },
      {
        heading: "3.3 Agentic Orchestration",
        items: [
          "Designing secure agentic frameworks and multi agent orchestration patterns.",
          "Controlling tool/function calling permissions and boundaries for autonomous agents.",
        ],
      },
    ],
  },
  {
    n: "04",
    weight: "15%",
    title: "AI Risk Management, Governance & Ethics",
    short: "Governance",
    groups: [
      {
        heading: "4.1 Enterprise Risk Frameworks",
        items: [
          "Implementing the NIST AI Risk Management Framework (RMF).",
          "Mapping ISO AI standards (e.g. ISO/IEC 42001) to enterprise environments.",
          "Designing enterprise controls for AI risk identification and mitigation.",
        ],
      },
      {
        heading: "4.2 Responsible AI",
        items: [
          "Addressing bias and fairness in AI models.",
          "Ethical considerations and establishing AI policy frameworks.",
        ],
      },
    ],
  },
  {
    n: "05",
    weight: "20%",
    title: "AI Audit, Compliance & Operations",
    short: "Audit & Ops",
    groups: [
      {
        heading: "5.1 Compliance and Regulations",
        items: [
          "Navigating the EU AI Act and its compliance obligations.",
          "Data privacy in AI (GDPR, CCPA) and the right to be forgotten in LLMs.",
          "Mapping AI regulations to existing organizational governance structures.",
        ],
      },
      {
        heading: "5.2 Integrating AI into Security Operations",
        items: [
          "Utilizing AI tools for SOC, Threat Intelligence and OSINT.",
          "AI assisted VAPT, Incident Response (IR) and Log Analysis.",
          "Automating threat analysis and report generation securely.",
        ],
      },
      {
        heading: "5.3 Auditing and Incident Management",
        items: [
          "How to audit AI systems end to end.",
          "Continuous monitoring and logging for LLM interactions.",
          "Executing AI breach case simulations and IR playbooks for AI specific breaches.",
        ],
      },
    ],
  },
]

export const MARQUEE_WORDS = [
  "Real World Simulations",
  "Practical Labs",
  "Expert Led Training",
  "AI Red Teaming",
  "Adversarial ML",
  "Guardrail Engineering",
];

